import {
  Accordion,
  Button,
  Combine,
  Content,
  CopyButton,
  FieldDescription,
  Flex,
  Form,
  Heading,
  Label,
  Link,
  Section,
  Text,
  TextArea,
  TextField,
} from "@mittwald/flow-remote-react-components";
import { useState } from "react";
import { useConfig } from "@mittwald/ext-bridge/react";
import { IconDownload, IconShare } from "@tabler/icons-react";
import { generateMobileConfig } from "../utils/generateMobileConfig.ts";
import { useMailAddress } from "../hooks/useMailAddress";
import { deriveDisplayNameFromEmail } from "../utils/deriveDisplayNameFromEmail.ts";

interface EmailFormData {
  displayName: string;
  password?: string;
}

// Static server configuration for mittwald email
const MITTWALD_EMAIL_SERVERS = {
  incoming: {
    host: "mail.agenturserver.de",
    port: 993,
    ssl: true,
  },
  outgoing: {
    host: "mail.agenturserver.de",
    port: 465,
    ssl: true,
  },
};

export const MobileConfigGenerator = () => {
  const config = useConfig();

  const emailAddressId =
    config.mailAddressId ?? "fb66000d-dd2f-4008-8313-398f9849126e";

  if (!emailAddressId) {
    throw new Error("E-Mail-Adresse ist nicht in der Konfiguration gesetzt");
  }

  const emailAddress = useMailAddress(emailAddressId);

  const defaultFormData = (): EmailFormData => ({
    displayName: deriveDisplayNameFromEmail(
      emailAddress.data.address,
      emailAddress.data.projectName,
    ),
    password: "",
  });

  const [formData, setFormData] = useState<EmailFormData>(defaultFormData);
  const [syncedEmailAddress, setSyncedEmailAddress] = useState(
    emailAddress.data,
  );

  // Changed mail address data starts the form over. Done during render instead
  // of in an effect, see https://react.dev/learn/you-might-not-need-an-effect.
  if (syncedEmailAddress !== emailAddress.data) {
    setSyncedEmailAddress(emailAddress.data);
    setFormData(defaultFormData());
  }

  // Create a URL-friendly slug from a string
  const createSlug = (text: string): string => {
    return text
      .toLowerCase()
      .replace(/[äöüß]/g, (match) => {
        const umlautMap: Record<string, string> = {
          ä: "ae",
          ö: "oe",
          ü: "ue",
          ß: "ss",
        };
        return umlautMap[match];
      })
      .replace(/[^a-z0-9\s-]/g, "") // Remove special characters
      .replace(/\s+/g, "-") // Replace spaces with hyphens
      .replace(/-+/g, "-") // Replace multiple hyphens with single hyphen
      .replace(/^-|-$/g, ""); // Remove leading/trailing hyphens
  };

  const handleGenerateConfig = () => {
    const configData = {
      emailAddress: emailAddress.data.address,
      displayName: formData.displayName,
      password: formData.password === "" ? undefined : formData.password,
      organization: emailAddress.data.projectName,
      incomingServer: {
        ...MITTWALD_EMAIL_SERVERS.incoming,
        username: emailAddress.data.address,
      },
      outgoingServer: {
        ...MITTWALD_EMAIL_SERVERS.outgoing,
        username: emailAddress.data.address,
      },
    };

    const mobileConfigContent = generateMobileConfig(configData);

    // Create blob and download with slugified filename
    const blob = new Blob([mobileConfigContent], {
      type: "application/x-apple-aspen-config",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${createSlug(formData.displayName)}.mobileconfig`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const additionalDescription = `E-Mail-Adresse: ${emailAddress.data.address}
Absendername: ${formData.displayName}
${formData.password ? `Passwort: ${formData.password}\n` : ""}
IMAP-Server (Posteingang):
Server: ${MITTWALD_EMAIL_SERVERS.incoming.host}
Port: ${String(MITTWALD_EMAIL_SERVERS.incoming.port)}
Verschlüsselung: SSL/TLS (nicht STARTTLS!)
Benutzername: ${emailAddress.data.address}

SMTP-Server (Postausgang):
Server: ${MITTWALD_EMAIL_SERVERS.outgoing.host}
Port: ${String(MITTWALD_EMAIL_SERVERS.outgoing.port)}
Verschlüsselung: SSL/TLS (nicht STARTTLS!)
Benutzername: ${emailAddress.data.address}`;

  return (
    <Form
      onSubmit={() => {
        handleGenerateConfig();
      }}
    >
      <Section>
        <Heading>E-Mail-Konfiguration für Apple-Geräte</Heading>
        <Text>
          Mit dieser Konfiguration kannst du ein E-Mail-Konto auf Apple-Geräten
          (iPhone, iPad, Mac) einrichten. Trage die Daten ein und lade die
          Konfiguration herunter. Anschließend kannst du sie verschicken oder
          direkt auf einem Apple-Gerät installieren.
        </Text>
        <Text>
          Weitere Informationen erhälst du in der{" "}
          <Link
            target="_blank"
            href="https://support.apple.com/de-de/guide/mac-help/mh35561/mac"
          >
            Apple-Doku zu Konfigurationsprofilen
          </Link>
          .
        </Text>
        <Heading level={3}>1. E-Mail-Daten eingeben</Heading>

        <TextField
          value={formData.displayName}
          onChange={(value) => {
            setFormData({ ...formData, displayName: value });
          }}
          isRequired
        >
          <Label>Absendername</Label>
          <FieldDescription>
            Name, der als E-Mail-Absender angezeigt wird
          </FieldDescription>
        </TextField>

        <TextField
          type="password"
          value={formData.password}
          onChange={(value) => {
            setFormData({ ...formData, password: value });
          }}
        >
          <Label>Passwort</Label>
          <FieldDescription>
            Passwort für die E-Mail-Adresse. Wenn leer, muss es beim Import
            eingegeben werden.
          </FieldDescription>
        </TextField>
      </Section>

      <Section>
        <Heading level={3}>2. Konfiguration teilen</Heading>

        <Text>
          Schicke die Konfigurationsdatei an deinen Kunden. Bitte beachte, dass
          die Datei ggf. ein Passwort enthält. Verschicke die Datei also nur
          über einen vertrauenswürdigen Kanal.
        </Text>

        <Flex gap="l" direction="row">
          <Button type="submit" variant="solid">
            <IconDownload size={18} />
            Konfiguration herunterladen
          </Button>
          <Button isDisabled>
            <IconShare size={18} />
            Download-Link teilen (bald verfügbar)
          </Button>
        </Flex>
      </Section>

      <Section>
        <Heading level={3}>3. (optional) manuelle Server-Konfiguration</Heading>
        <Text>
          Falls du einen anderen Client einrichten möchtest, verwende diese
          Einstellungen. Du kannst diese Daten auch direkt an deinen Kunden
          schicken. Nutze dafür am besten einen sicheren Übertragungsweg wie{" "}
          <Link target="_blank" href="https://share-secret.mittwald.de/">
            Share Secret von mittwald
          </Link>
        </Text>
        <Combine>
          <CopyButton text={additionalDescription} />
          <Text>Anleitung kopieren</Text>
        </Combine>
        <Accordion>
          <Label>Anleitung zur manuellen Einrichtung ausklappen</Label>
          <Content>
            <Flex gap="l" direction="column">
              <TextArea value={additionalDescription} isReadOnly rows={15} />
            </Flex>
          </Content>
        </Accordion>
      </Section>
    </Form>
  );
};
